import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface OceanZone {
  name: string;
  depth: string;
  color: string;
  light: string;
}

interface MarineCreature {
  name: string;
  depth: string;
  feature: string;
  icon: string;
  color: string;
}

interface ExplorationVehicle {
  name: string;
  maxDepth: string;
  year: string;
  country: string;
}

interface OceanFact {
  label: string;
  icon: string;
}

interface DeepOceanTemplateProps {
  topic: string;
  subtitle: string;
  oceanZones: OceanZone[];
  creatures: MarineCreature[];
  vehicles: ExplorationVehicle[];
  oceanFacts: OceanFact[];
  pressureData: { depth: number; atm: number }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: DeepOceanTemplateProps = {
  topic: '深海探索与海洋生物',
  subtitle: 'Deep Ocean Exploration & Marine Life',
  oceanZones: [
    { name: '光合带', depth: '0-200m', color: '#0ea5e9', light: '100%' },
    { name: '中层带', depth: '200-1000m', color: '#1d4ed8', light: '1%' },
    { name: '深层带', depth: '1000-4000m', color: '#1e3a5f', light: '0%' },
    { name: '深渊带', depth: '4000-6000m', color: '#0f172a', light: '0%' },
    { name: '超深渊带', depth: '6000-11000m', color: '#020617', light: '0%' },
  ],
  creatures: [
    {
      name: '抹香鲸',
      depth: '~2250m',
      feature: '哺乳动物最深潜水记录',
      icon: '🐋',
      color: '#38bdf8',
    },
    {
      name: '巨乌贼',
      depth: '~1000m',
      feature: '眼球直径达 30cm',
      icon: '🦑',
      color: '#c084fc',
    },
    {
      name: '琵琶鱼',
      depth: '~2000m',
      feature: '头顶生物发光器诱饵',
      icon: '🐟',
      color: '#22d3ee',
    },
    {
      name: '管虫',
      depth: '~2500m',
      feature: '热泉口化能合成共生',
      icon: '🪱',
      color: '#f87171',
    },
  ],
  vehicles: [
    { name: '的里雅斯特号', maxDepth: '10916m', year: '1960', country: '🇺🇸' },
    { name: '蛟龙号', maxDepth: '7062m', year: '2012', country: '🇨🇳' },
    { name: '奋斗者号', maxDepth: '10909m', year: '2020', country: '🇨🇳' },
    { name: '限制因子号', maxDepth: '10928m', year: '2019', country: '🇺🇸' },
  ],
  oceanFacts: [
    { label: '生物发光', icon: '✨' },
    { label: '热液喷口', icon: '🌋' },
    { label: '极端嗜压', icon: '💎' },
    { label: '冷泉生态', icon: '🫧' },
  ],
  pressureData: [
    { depth: 0, atm: 1 },
    { depth: 1000, atm: 100 },
    { depth: 4000, atm: 400 },
    { depth: 7000, atm: 700 },
    { depth: 11000, atm: 1100 },
  ],
  stats: [
    { value: '71%', label: '海洋覆盖地球表面' },
    { value: '10,994m', label: '马里亚纳海沟深度' },
    { value: '95%', label: '深海尚未被探索' },
  ],
  bottomQuote:
    '海洋深处是地球上最后的未知疆域——那里的生命以我们难以想象的方式繁衍生息',
};

export const DeepOceanTemplate: React.FC<Partial<DeepOceanTemplateProps>> = (
  props,
) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const zoneStart = 30;
  const creatureStart = 160;
  const vehicleStart = 280;
  const factStart = 370;
  const pressureStart = 200;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const pressureDraw = interpolate(
    frame,
    [pressureStart, pressureStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const pressureCurvePath =
    'M60,180 Q160,178 260,145 Q400,90 540,55 Q650,28 760,12';

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #0c1929 0%, #020617 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Bioluminescence particles */}
      {Array.from({ length: 55 }).map((_, i) => {
        const x = (i * 307) % 1920;
        const y = (i * 173) % 1080;
        const size = 2 + (i % 5);
        const drift = Math.sin(frame * 0.03 + i) * 8;
        const pulse = 0.08 + 0.06 * Math.sin(frame * 0.05 + i * 0.7);
        const colors = ['#22d3ee', '#38bdf8', '#a78bfa', '#34d399'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}40`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#0369a1',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#7dd3fc',
            flexShrink: 0,
          }}
        >
          🌊 深海科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#e0f2fe',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 24,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Ocean Zones Depth Diagram ===== */}
      <div style={{ position: 'absolute', top: 160, left: 32, width: 500 }}>
        <div
          style={{
            fontSize: 26,
            fontWeight: 700,
            color: '#cbd5e1',
            marginBottom: 12,
            letterSpacing: '0.08em',
          }}
        >
          海洋分层
        </div>
        <svg
          width="480"
          height="620"
          viewBox="0 0 480 620"
          shapeRendering="geometricPrecision"
          style={{ overflow: 'hidden' }}
        >
          <defs>
            <linearGradient id="oceanDepthGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.92" />
              <stop offset="12%" stopColor="#0ea5e9" stopOpacity="0.85" />
              <stop offset="28%" stopColor="#2563eb" stopOpacity="0.78" />
              <stop offset="48%" stopColor="#1e40af" stopOpacity="0.72" />
              <stop offset="68%" stopColor="#1e3a5f" stopOpacity="0.82" />
              <stop offset="88%" stopColor="#0f172a" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#020617" stopOpacity="0.98" />
            </linearGradient>
            <linearGradient id="oceanSurfaceSheen" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
              <stop offset="35%" stopColor="#e0f2fe" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
          </defs>

          <rect
            x="68"
            y="12"
            width="248"
            height="580"
            rx="14"
            fill="url(#oceanDepthGrad)"
            strokeWidth="2.5"
            stroke="#7dd3fc55"
            opacity={interpolate(frame, [zoneStart, zoneStart + 40], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />
          <rect
            x="68"
            y="12"
            width="248"
            height="76"
            rx="14"
            fill="url(#oceanSurfaceSheen)"
            opacity={interpolate(frame, [zoneStart, zoneStart + 35], [0, 0.85], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />

          {p.oceanZones.map((zone, i) => {
            const zoneH = 580 / p.oceanZones.length;
            const yPos = 12 + i * zoneH;
            const yMid = yPos + zoneH / 2;
            const s = spring({
              frame: frame - (zoneStart + 20 + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <g key={i} opacity={s}>
                {i > 0 ? (
                  <line
                    x1="68"
                    y1={yPos}
                    x2="316"
                    y2={yPos}
                    stroke="#ffffff"
                    strokeWidth="1"
                    strokeOpacity="0.08"
                  />
                ) : null}
                <line
                  x1="316"
                  y1={yMid}
                  x2="338"
                  y2={yMid}
                  stroke={zone.color}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <text
                  x="346"
                  y={yMid - 6}
                  fill="#e0f2fe"
                  fontSize="22"
                  fontWeight="700"
                  style={{ fontFamily: 'inherit' }}
                >
                  {zone.name}
                </text>
                <text
                  x="346"
                  y={yMid + 20}
                  fill="#94a3b8"
                  fontSize="18"
                  style={{ fontFamily: 'inherit' }}
                >
                  {zone.depth}
                </text>
                <text
                  x="346"
                  y={yMid + 42}
                  fill="#64748b"
                  fontSize="18"
                  style={{ fontFamily: 'inherit' }}
                >
                  光照: {zone.light}
                </text>

                <line
                  x1="48"
                  y1={yMid}
                  x2="48"
                  y2={yMid}
                  stroke="#64748b"
                  strokeWidth="1.2"
                  strokeDasharray="4 3"
                />
                <text
                  x="54"
                  y={yMid + 6}
                  fill="#94a3b8"
                  fontSize="15"
                  fontWeight="600"
                  textAnchor="end"
                  style={{ fontFamily: 'inherit' }}
                >
                  {zone.depth.split('-')[0] || '0'}m
                </text>
              </g>
            );
          })}

          {[128, 188, 248].map((x, i) => (
            <line
              key={`ray-${i}`}
              x1={x}
              y1="12"
              x2={x + 14}
              y2="118"
              stroke="#fcd34d"
              strokeWidth="2"
              strokeLinecap="round"
              opacity={interpolate(
                frame,
                [zoneStart + 10, zoneStart + 40],
                [0, 0.32 - i * 0.06],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            />
          ))}
        </svg>
      </div>

      {/* ===== CENTER: Creatures + Pressure Curve ===== */}
      <div style={{ position: 'absolute', top: 160, left: 548, width: 852 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          深海生物
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
          }}
        >
          {p.creatures.map((cr, i) => {
            const s = spring({
              frame: frame - (creatureStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: '#0f172a',
                  borderRadius: 14,
                  padding: '12px 14px',
                  border: `1.5px solid ${cr.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${cr.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 30,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${cr.color}30`,
                  }}
                >
                  {cr.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span
                      style={{ fontSize: 24, fontWeight: 800, color: cr.color }}
                    >
                      {cr.name}
                    </span>
                    <span style={{ fontSize: 22, color: '#64748b' }}>
                      {cr.depth}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 22, color: '#94a3b8', marginTop: 2 }}
                  >
                    {cr.feature}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pressure Curve */}
        <div style={{ marginTop: 28 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            水压曲线 · 深度 vs 大气压
          </div>
          <svg width="852" height="260" viewBox="0 0 800 200" preserveAspectRatio="xMidYMid meet">
            {[40, 80, 120, 160].map((y) => (
              <line
                key={y}
                x1="60"
                y1={y}
                x2="780"
                y2={y}
                stroke="#1e3a5f"
                strokeWidth="1.2"
                opacity="0.3"
              />
            ))}
            <text x="2" y="185" fill="#64748b" fontSize="20">
              0m
            </text>
            <text x="2" y="145" fill="#64748b" fontSize="20">
              3000m
            </text>
            <text x="2" y="100" fill="#64748b" fontSize="20">
              6000m
            </text>
            <text x="2" y="55" fill="#64748b" fontSize="20">
              9000m
            </text>
            <text x="2" y="20" fill="#64748b" fontSize="20">
              11000m
            </text>

            <text x="780" y="198" fill="#64748b" fontSize="20" textAnchor="end">
              1100 atm →
            </text>

            <path
              d={pressureCurvePath}
              fill="none"
              stroke="#22d3ee"
              strokeWidth="4"
              strokeDasharray="1200"
              strokeDashoffset={1200 * (1 - pressureDraw)}
              strokeLinecap="round"
            />

            {/* Mariana Trench marker */}
            <g
              opacity={interpolate(
                frame,
                [pressureStart + 70, pressureStart + 90],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              <circle cx="760" cy="12" r="6.5" fill="#f87171" />
              <text x="668" y="5" fill="#f87171" fontSize="20" fontWeight="700">
                马里亚纳海沟
              </text>
            </g>
          </svg>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Exploration Vehicles + Ocean Facts ===== */}
      <div style={{ position: 'absolute', top: 160, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          深潜器
        </div>
        {p.vehicles.map((v, i) => {
          const s = spring({
            frame: frame - (vehicleStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0f172a',
                borderRadius: 12,
                padding: '12px 16px',
                border: '1.5px solid #1e3a5f',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>{v.country}</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: '#7dd3fc' }}>
                  {v.name}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                <span style={{ fontSize: 22, color: '#22d3ee', fontWeight: 700 }}>
                  {v.maxDepth}
                </span>
                <span style={{ fontSize: 22, color: '#64748b' }}>{v.year}年</span>
              </div>
            </div>
          );
        })}

        {/* Ocean Facts */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            深海现象
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.oceanFacts.map((fact, i) => {
              const s = spring({
                frame: frame - (factStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0f172a',
                    borderRadius: 12,
                    padding: '14px 10px',
                    border: '1px solid #1e3a5f',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 26, lineHeight: 1 }}>{fact.icon}</span>
                  <span
                    style={{ fontSize: 22, fontWeight: 700, color: '#7dd3fc' }}
                  >
                    {fact.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 90,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f172a',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #1e3a5f',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 30, fontWeight: 900, color: '#22d3ee' }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          left: 36,
          right: 36,
          background: '#0f172a',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #0369a1',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🐚</span>
          <span style={{ fontSize: 20, color: '#e0f2fe', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #0369a1, #22d3ee, #a78bfa)',
        }}
      />
    </AbsoluteFill>
  );
};
